import { useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { useFindCase } from '@/application/usecases/useKits';
import { useSession } from '@/application/usecases/useSession';
import { canOpenView, casePath } from '@/domain';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { Modal } from '@/ui/primitives/Modal';
import { ScanIcon } from '@/ui/icons';
import styles from './FindCaseDialog.module.css';

/**
 * The search behind the magnifying glass in the top bar.
 *
 * It finds a CASE, not text. The client's words were "it should work by
 * scanning the job orders", and what is printed on a job order and on a tray
 * card is a job order number, a kit id and a QR value. Those three are what
 * this accepts, and a scan of the same codes is one tap away.
 *
 * A job order opens on the orders screen for somebody who holds it and on the
 * case itself for somebody who does not, because a technician typing JO-2054
 * wants the tray, not a list they cannot see. Neither route claims the case:
 * finding is not taking.
 */
export function FindCaseDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const session = useSession();
  const find = useFindCase();
  const [code, setCode] = useState('');
  const inputId = useId();
  /*
   * Scan, for whoever can open the scanner. It was offered to everybody, and
   * took Reception and Accounts to a screen that sent them straight home. The
   * request of 5 October 2026 (third), section 10.
   */
  const scans = canOpenView(session.data?.views ?? [], 'scan');

  function submit() {
    const value = code.trim();
    if (value === '') return;

    find.mutate(value, {
      onSuccess: ({ kitId, jobOrderNo }) => {
        onClose();
        // A job order number prefers the order, a tray code the tray, and
        // either opens whichever door this person has: Reception and Accounts
        // hold no bench, Accounts not even Orders.
        const path = casePath(
          session.data?.views ?? [],
          { jobOrderNo, kitId },
          /^JO-/i.test(value) ? 'order' : 'kit',
        );
        if (path !== null) void navigate(path);
      },
    });
  }

  return (
    <Modal
      title={strings.find.title}
      lead={scans ? strings.find.lead : strings.find.leadTyped}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{strings.find.close}</Button>
          <Button variant="primary" onClick={submit} disabled={find.isPending || code.trim() === ''}>
            {strings.find.open}
          </Button>
        </>
      }
    >
      <label htmlFor={inputId} className={styles.l}>
        {strings.find.inputLabel}
      </label>
      <input
        id={inputId}
        className={styles.input}
        value={code}
        autoFocus
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        placeholder={strings.find.placeholder}
        onChange={(event) => setCode(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') submit();
        }}
      />

      {find.isError && (
        <p className={styles.error} role="alert">
          {strings.find.unknown}
        </p>
      )}

      {scans && (
        <div className={styles.or}>
          <Button
            icon={<ScanIcon />}
            onClick={() => {
              onClose();
              void navigate('/scan');
            }}
          >
            {strings.find.scan}
          </Button>
        </div>
      )}
    </Modal>
  );
}
